import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import QuickFacts from '../components/QuickFacts';
import { articlesCardData } from '../content/articles/ArticlesCardData';
import { destinationCardData } from '../content/destinations/DestinationCardData';
import BackButton from '../components/BackButton';
import ArticleCardsContainer from '../components/Article/ArticleCardContainer';
import styled from 'styled-components';
import { FaSearch } from 'react-icons/fa';
import Breadcrumbs from '../components/Breadcrumbs';
import { generateCountryBreadcrumbs } from '../utils/breadcrumbHelpers';

const SectionContainer = styled.div`
  margin-bottom: 3rem;
`;

const SearchContainer = styled.div`
  margin: 2rem 0;
  position: relative;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 48px 12px 20px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  font-size: 16px;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.3s ease;
  direction: rtl;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}30`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
`;

const BreadcrumbsContainer = styled.div`
  padding-top: 20px;
  margin-bottom: 20px;
`;

const CountryHub = () => {
  const { country } = useParams();
  const [searchQuery, setSearchQuery] = useState('');

  const destinations = useMemo(
    () =>
      country
        ? destinationCardData[country as keyof typeof destinationCardData]
        : [],
    [country]
  );

  const articles = useMemo(
    () =>
      country ? articlesCardData[country as keyof typeof articlesCardData] : [],
    [country]
  );

  const filteredDestinations = useMemo(() => {
    if (!searchQuery) return destinations;
    const query = searchQuery.toLowerCase();
    return destinations.filter(
      (destination) =>
        destination.title.toLowerCase().includes(query) ||
        destination.description.toLowerCase().includes(query)
    );
  }, [destinations, searchQuery]);

  const filteredArticles = useMemo(() => {
    if (!searchQuery) return articles;
    const query = searchQuery.toLowerCase();
    return articles.filter(
      (article) =>
        article.title.toLowerCase().includes(query) ||
        article.description.toLowerCase().includes(query)
    );
  }, [articles, searchQuery]);

  const getCountryInfo = (countryId: string) => {
    const countryMap: Record<string, { name: string }> = {
      thailand: { name: 'תאילנד' },
      vietnam: { name: 'וייטנאם' },
      cambodia: { name: 'קמבודיה' },
    };
    return countryMap[countryId] || { name: '' };
  };

  const countryInfo = getCountryInfo(country || '');
  const hasResults =
    filteredDestinations.length > 0 || filteredArticles.length > 0;

  // Generate breadcrumbs only if country exists
  const breadcrumbItems = country ? generateCountryBreadcrumbs(country) : [];

  return (
    <Container>
      <BreadcrumbsContainer>
        <Breadcrumbs items={breadcrumbItems} />
      </BreadcrumbsContainer>
      {/* <BackButton to="/international" /> */}
      <QuickFacts countryCode={country || ''} />

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder={`חיפוש ב${countryInfo.name}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <SearchIcon>
          <FaSearch />
        </SearchIcon>
      </SearchContainer>

      {!hasResults && searchQuery && (
        <NoResults>לא נמצאו תוצאות עבור "{searchQuery}"</NoResults>
      )}

      {(!searchQuery || filteredDestinations.length > 0) && (
        <SectionContainer>
          <ArticleCardsContainer
            title={`יעדים ב${countryInfo.name}`}
            articles={filteredDestinations}
            rowProps={{ xs: 1, md: 2, lg: 4 }}
          />
        </SectionContainer>
      )}

      {(!searchQuery || filteredArticles.length > 0) && (
        <SectionContainer>
          <ArticleCardsContainer
            title={`דברים שכדאי לדעת על ${countryInfo.name}`}
            articles={filteredArticles}
            rowProps={{ xs: 1, md: 2, lg: 3 }}
          />
        </SectionContainer>
      )}
    </Container>
  );
};

export default CountryHub;
